import {
    IApplicationModuleSession,
    IApplicationSession,
    IPlatformSession,
    IPlatformViewport,
    IPlatformViewportCreateAppOptions,
    IPlatformViewportEvent,
    IPlatformViewportRenderer,
    IUIOperationOptions,
    PlatformViewportEventAction,
    ViewportStartupOptions,
} from "./PA.UIM.Core.IF.Platform";
import * as _ from "lodash";
import { BehaviorSubject } from "rxjs";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { ISearchRendererOptions } from "./PA.UIM.Core.IF.Search";
import { PlatformSession } from "./PlatformSession";
import { IApplication, LaunchPolicy } from "./PA.UIM.Core.IF.Framework";
import { ApplicationSession } from "./ApplicationSession";
import { UIFrameworkService } from "../PA.UIFramework.Services/UIFrameworkService";
import { IUsageDetails } from "../PA.UIFramework.Services/PA.UIFramework.IF.Services";

export class PlatformViewport implements IPlatformViewport {
    private _viewportId: string;
    private _renderer: IPlatformViewportRenderer;
    private _platformSession: IPlatformSession;

    private _applicationSessions: IApplicationSession[];

    private _onSessionStateChange: BehaviorSubject<IPlatformViewportEvent>;

    constructor(platformSession: IPlatformSession, renderer: IPlatformViewportRenderer, options: any) {
        this._viewportId = `Viewport${PlatformSession.getNextSessionId()}`;
        this._platformSession = platformSession;
        this._renderer = renderer;

        this._applicationSessions = [];
        this._onSessionStateChange = new BehaviorSubject<IPlatformViewportEvent>(undefined);
    }

    get viewportId(): string {
        return this._viewportId;
    }

    get Renderer(): IPlatformViewportRenderer {
        return this._renderer;
    }

    get PlatformSession(): IPlatformSession {
        return this._platformSession;
    }

    get ApplicationSessions(): IApplicationSession[] {
        return this._applicationSessions;
    }

    get CurrentApplicationSession(): IApplicationSession {
        return this.Renderer.getInFocusApplicationSession();
    }

    get OnSessionStateChange(): BehaviorSubject<IPlatformViewportEvent> {
        return this._onSessionStateChange;
    }

    RunStartup(options?: ViewportStartupOptions): void {
        const self = this;
        let opts = options || {};

        let dctLaunchedwbAppIds: { [id: number]: boolean } = {};
        _.forEach(self.PlatformSession.Framework.ApplicationSuites, (aSuite) => {
            _.forEach(aSuite.applicationRels, (aRel) => {
                if (_.isEmpty(aRel)) {
                    return;
                }
                if (!aRel.bAutoLaunch) {
                    return;
                }
                if (aRel.application.wb_id in dctLaunchedwbAppIds) {
                    return;
                }
                dctLaunchedwbAppIds[aRel.application.wb_id] = true;
                self.CreateApplication(aRel.application.wb_id, { AppSuiteAppRel: aRel });
            });
        });

        // Go through the framework and Launch AutoStart Apps
        _.forEach(self.PlatformSession.Framework.Applications, (anApp) => {
            if (anApp.wb_id in dctLaunchedwbAppIds) {
                return;
            }
            if (anApp.startupPolicy == "AutoStart") {
                dctLaunchedwbAppIds[anApp.wb_id] = true;
                self.CreateApplication(anApp.wb_id, {});
            }
        });
    }

    //--  Create Application
    //--  Find if application is running, if so, check Launch Policy
    //00      if
    //--  else
    //--     Create new Application and run AutoLaunching
    //--

    CreateApplication(wbAppId: number, options?: IPlatformViewportCreateAppOptions): IApplicationSession {
        const self = this;
        let opts = options || {};

        let anApplication = self.PlatformSession.Framework.getApplication(wbAppId);
        let anApplicationSession: IApplicationSession = undefined;
        let sLaunchPolicy = opts.AppSuiteAppRel?.launchPolicy || anApplication.launchPolicy;

        _.forEach(self._applicationSessions, (a) => {
            if (a.Application.wb_id == wbAppId) {
                // Found running App
                anApplicationSession = a;
                return false;
            }
        });

        if (!_.isEmpty(anApplicationSession) && sLaunchPolicy == LaunchPolicy.SingleInstance) {
            //--
            return anApplicationSession;
        }

        //--
        //-- Create Application Session
        anApplicationSession = new ApplicationSession(self, anApplication, opts);
        self._applicationSessions.push(anApplicationSession);
        self._renderer.renderApplication(anApplicationSession, opts);
        if (!opts.bDisableAutoLauch && opts.AppSuiteAppRel?.bAutoLaunch) {
            //-- TODO: Handle AutoLaunch Behavior
        }

        self.OnSessionStateChange.next({
            action: PlatformViewportEventAction.ApplicationSessionCreated,
            appSession: anApplicationSession,
            viewport: self,
        });

        //Add usage for the app Launch
        let fs = new UIFrameworkService();
        let usageDetails: IUsageDetails = {
            Usage: "AppLaunch",
            wbUIAppId: wbAppId,
        };
        fs.LogUsageSync(usageDetails);
        fs.SaveUsage();

        return anApplicationSession;
    }

    async CloseApplication(sessionId: number): Promise<boolean> {
        const self = this;

        let appSession: IApplicationSession = undefined;
        for (let anAppSession of self._applicationSessions) {
            if (anAppSession.SessionId == sessionId) {
                appSession = anAppSession;
                break;
            }
        }
        if (!appSession) {
            return false;
        }

        let returnValue = true;
        while (appSession.ApplicationModuleSessions.length) {
            let moduleSession = _.first(appSession.ApplicationModuleSessions);
            let bClose = await moduleSession.Close();
            returnValue = returnValue && bClose;
            if (!returnValue) {
                break;
            } else {
                appSession.PruneSession(moduleSession);
            }
        }

        if (returnValue) {
            self.PruneApplicationSession(appSession);
            self.Renderer.destroyApplication(appSession);
        }

        return returnValue;
    }

    private PruneApplicationSession(anApplicationSession: IApplicationSession): void {
        const self = this;
        _.remove(self._applicationSessions, anApplicationSession);
        self.OnSessionStateChange.next({
            action: PlatformViewportEventAction.ApplicationSessionClosed,
            appSession: anApplicationSession,
        });
    }

    //--  Check open Sesison for Executable

    private FindOrCreateApplicationSessionWithExecutable(anExecutableId: string): IApplicationSession {
        const self = this;
        let returnValue: IApplicationSession = undefined;

        _.forEach(self._applicationSessions, (appSession) => {
            _.forEach(appSession.Application.applicationModules, (aMod) => {
                if (aMod.executable.id_ui == anExecutableId) {
                    returnValue = appSession;
                    return false; // stop iterating
                }
            });
            if (!_.isEmpty(returnValue)) {
                return false; // stop iterating
            }
        });

        if (!_.isEmpty(returnValue)) {
            return returnValue;
        }

        // Launched App
        let foundApp: IApplication = undefined;
        _.forEach(self.PlatformSession.Framework.Applications, (anApp) => {
            _.forEach(anApp.applicationModules, (aModule) => {
                if (aModule.executable.id_ui == anExecutableId) {
                    foundApp = anApp;
                    return false;
                }
            });
            if (!_.isEmpty(foundApp)) {
                return false;
            }
        });

        if (_.isEmpty(foundApp)) {
            return undefined;
        }

        returnValue = self.CreateApplication(foundApp.wb_id, { bDisableAutoLauch: true });

        return returnValue;
    }

    private FindApplicationSessionWithExecutableObject(
        anExecutableId: string,
        wbObjectIdOrBO: number | string | IBusinessObject,
    ): IApplicationSession {
        const self = this;

        let returnValue: IApplicationSession = undefined;
        let compareById = _.isNumber(wbObjectIdOrBO) || _.isString(wbObjectIdOrBO);

        _.forEach(self._applicationSessions, (appSession) => {
            _.forEach(appSession.ApplicationModuleSessions, (aModSession) => {
                let executableId = aModSession.ApplicationModule?.executable?.id_ui;
                if (executableId != anExecutableId) {
                    return;
                }

                if (compareById && aModSession.isObject(<any>wbObjectIdOrBO)) {
                    returnValue = appSession;
                    return false; // stop iterating
                }
                if (!compareById && aModSession.isBusinessObject(<any>wbObjectIdOrBO)) {
                    returnValue = appSession;
                    return false; // stop iterating
                }
            });

            if (!_.isEmpty(returnValue)) {
                return false; // stop iterating
            }
        });

        return returnValue;
    }

    Create(anExecutableId: string, options?: IUIOperationOptions): IApplicationModuleSession {
        const self = this;

        let foundApplication = self.FindOrCreateApplicationSessionWithExecutable(anExecutableId);
        if (_.isEmpty(foundApplication)) {
            //-- No Access (log message?)
            return;
        }
        return foundApplication.Create(anExecutableId, options);
    }

    Open(anExecutableId: string, BOid: string | number, options?: IUIOperationOptions): IApplicationModuleSession {
        const self = this;

        // Walk through sessions to see if Object is open.
        let returnValue = self.FindSessionByObjectId(anExecutableId, BOid);
        if (returnValue) {
            self.Renderer.setFocus(returnValue);
            return returnValue;
        }

        let foundApplication = self.FindOrCreateApplicationSessionWithExecutable(anExecutableId);
        if (_.isEmpty(foundApplication)) {
            //-- No Access (log message?)
            return;
        }
        return foundApplication.Open(anExecutableId, BOid, options);
    }

    OpenBO(anExecutableId: string, aBO: IBusinessObject, options?: IUIOperationOptions): IApplicationModuleSession {
        const self = this;

        let returnValue = self.FindSessionByBO(anExecutableId, aBO);
        if (returnValue) {
            self.Renderer.setFocus(returnValue);
            return returnValue;
        }

        let foundApplication = self.FindOrCreateApplicationSessionWithExecutable(anExecutableId);
        if (_.isEmpty(foundApplication)) {
            //-- No Access (log message?)
            return;
        }
        return foundApplication.OpenBO(anExecutableId, aBO, options);
    }

    async Close(anExecutableId: string, BOid: string | number, options?: IUIOperationOptions): Promise<boolean> {
        const self = this;

        let foundApplication = self.FindApplicationSessionWithExecutableObject(anExecutableId, BOid);
        if (_.isEmpty(foundApplication)) {
            //-- No Access (log message?)
            return;
        }
        return await foundApplication.Close(anExecutableId, BOid, options);
    }

    FindSessionByObjectId(anExecutableId: string, id: string | number): IApplicationModuleSession {
        const self = this;

        let appModuleSession: IApplicationModuleSession = undefined;

        _.forEach(self._applicationSessions, (appSession) => {
            _.forEach(appSession.ApplicationModuleSessions, (ams) => {
                if (ams?.ApplicationModule?.executable?.id_ui == anExecutableId) {
                    if (ams.isObject(id)) {
                        appModuleSession = ams;
                        return false;
                    }
                }
            });
            if (!_.isEmpty(appModuleSession)) {
                return false;
            }
        });

        return appModuleSession;
    }

    FindSessionByBO(anExecutableId: string, aBO: IBusinessObject): IApplicationModuleSession {
        const self = this;

        let appModuleSession: IApplicationModuleSession = undefined;

        _.forEach(self._applicationSessions, (appSession) => {
            _.forEach(appSession.ApplicationModuleSessions, (ams) => {
                if (ams?.ApplicationModule?.executable?.id_ui == anExecutableId) {
                    if (ams.isBusinessObject(aBO)) {
                        appModuleSession = ams;
                        return false;
                    }
                }
            });
            if (!_.isEmpty(appModuleSession)) {
                return false;
            }
        });

        return appModuleSession;
    }

    FindSessionBySessionId(id: number): IApplicationModuleSession {
        const self = this;

        let appModuleSession: IApplicationModuleSession = undefined;

        _.forEach(self._applicationSessions, (appSession) => {
            _.forEach(appSession.ApplicationModuleSessions, (ams) => {
                if (ams.SessionId == id) {
                    appModuleSession = ams;
                    return false;
                }
            });
            if (!_.isEmpty(appModuleSession)) {
                return false;
            }
        });

        return appModuleSession;
    }

    async SelectOne(anExecutableId: string, options?: ISearchRendererOptions): Promise<number | string> {
        const self = this;

        let searchOptions: any = {};
        let executable = self.PlatformSession.Framework.getExecutable(anExecutableId);
        if (_.isEmpty(executable)) {
            return Promise.resolve(0);
        }
        _.defaults(searchOptions, options || {}, executable.searchOptions);

        return self.Renderer.searchRenderer.SelectOne(executable.searchClass, searchOptions);
    }

    SelectNavigationList(anExecutableId: string, options?: ISearchRendererOptions): Promise<number[] | string[]> {
        const self = this;

        let searchOptions: any = {};
        let executable = self.PlatformSession.Framework.getExecutable(anExecutableId);
        if (_.isEmpty(executable)) {
            return Promise.resolve([]);
        }
        _.defaults(searchOptions, options || {}, executable.searchOptions);

        return self.Renderer.searchRenderer.SelectNavigationList(executable.searchClass, searchOptions);
    }

    SelectMany(anExecutableId: string, options?: ISearchRendererOptions): Promise<number[] | string[]> {
        const self = this;

        let searchOptions: any = {};
        let executable = self.PlatformSession.Framework.getExecutable(anExecutableId);
        if (_.isEmpty(executable)) {
            return Promise.resolve([]);
        }
        _.defaults(searchOptions, options || {}, executable.searchOptions);

        return self.Renderer.searchRenderer.SelectMany(executable.searchClass, searchOptions);
    }

    FindOrCreate(appIds: string | string[], executableIds: string | string[]): IApplicationModuleSession {
        const self = this;

        if (!_.isArray(appIds)) appIds = [appIds];

        if (!_.isArray(executableIds)) executableIds = [executableIds];

        //--
        let fw = self._platformSession.Framework;
        let dctAppSort: { [appId: string]: number } = {};
        _.forEach(appIds, (v, idx) => {
            dctAppSort[v] = idx;
        });

        for (let executableId of executableIds) {
            let arrAppMdls = fw.getExecutableAppModules(executableId);
            if (_.isEmpty(arrAppMdls)) continue;

            // Filter down to Apps in scope
            if (!("*" in dctAppSort)) arrAppMdls = _.filter(arrAppMdls, (am) => am.Application.id_ui in dctAppSort);

            arrAppMdls = _.sortBy(arrAppMdls, (am) => dctAppSort[am.Application.id_ui] || 999);
            if (arrAppMdls.length) {
                let application = arrAppMdls[0].Application;
                let appModule = arrAppMdls[0].AppModule;

                // Find or Create ApplicationSession
                let appSession: IApplicationSession;
                _.forEach(self.ApplicationSessions, (as) => {
                    if (as.Application.wb_id == application.wb_id) {
                        appSession = as;
                        return false;
                    }
                });
                if (_.isEmpty(appSession)) appSession = self.CreateApplication(application.wb_id, {});

                return appSession.CreateModuleSession(appModule.wb_id, { bIsSearchSession: false });
            } // Found
        } // Next Executable

        return undefined;
    }
}
